import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { DirectionsBus, SwapHoriz } from '@mui/icons-material';

import { CONFIG } from '@frontend/config';
import {
  BusesSearchPayload,
  CatalogEntry,
  isProvenanceExpired,
  useBusStationsResolution,
  useSearchBuses,
} from '../queries';
import {
  cleanParams,
  enumParam,
  isIncompleteSearch,
  listParam,
  numParam,
  serializeList,
  UrlParams,
  useSearchUrlState,
} from '../useSearchUrlState';
import {
  BusFilters,
  BusOffer,
  BusSort,
  EMPTY_BUS_FILTERS,
  arrivesNextDay,
  busChainWithFinalQuote,
  busChainWithOutbound,
  busFacetCount,
  busReturnPayload,
  busRouteMapPoints,
  busBookingDetailLines,
  busTime,
  captureFinalBusQuote,
  hasFewSeats,
  captureOutboundBusQuote,
  busDay,
  emptyBusQuoteChain,
  filterBuses,
  isDirect,
  mainBusSearchIdentity,
  reconcileBusChainWithMainSearch,
  seatClassName,
  sortBuses,
} from '../buses';
import { committedParty } from '../committedBookingContext';
import { formatMinutes } from '../format';
import { formatMoney } from '../money';
import { describeBuses } from '../describeSearch';
import { useAudience } from '../audience';
import { searchRouteMapKey } from '../routeMap';
import { BusStationInput } from './BusStationInput';
import { MetaLine } from './MetaLine';
import { PriceBlock } from './PriceBlock';
import { RuteroResultCard } from './RuteroResultCard';
import { ScheduleTimes } from './ScheduleTimes';
import { StatusFlag } from './StatusFlag';
import { ProviderStatusStrip } from './ProviderStatusStrip';
import { SearchBar, SearchDivider, SearchField } from './SearchBar';
import {
  SEARCH_DATE_FIELD_WIDTH,
  SEARCH_FIELD_HALF_SHARE,
  SEARCH_PASSENGER_FIELD_WIDTH,
} from '../searchFieldLayout';
import { RecentSearches } from './RecentSearches';
import {
  FilterChip,
  FilterGroup,
  ResultFiltersBar,
} from './ResultFiltersBar';
import { BookingModal } from './BookingModal';
import { quoteSearchIdentity } from '../selectedQuote';
import { isQuoteFreshForHold, useFareCountdown } from '../useFareCountdown';
import { useSearchResultAuthority } from '../useSearchResultAuthority';
import { searchFormDiverged, shouldRefetchExpiredIdenticalSearch } from '../searchResultAuthority';
import { SearchRouteMap } from './SearchRouteMap';
import { SearchResultGenerationStatus } from './SearchResultGenerationStatus';
import { ResultsSkeleton } from './ResultsSkeleton';
import { ActiveSearchSummary } from './ActiveSearchSummary';
import {
  latestSearchDate,
  reconcileSearchEndDate,
  todaySearchDate,
} from '../searchDates';
import { SearchDatePicker } from './SearchDatePicker';
import {
  COUNT_ONLY_PASSENGER_RULES,
  countOnlyPassengers,
} from '../searchPassengers';
import { SearchPassengerPicker } from './SearchPassengerPicker';
import { useBookingCaseContext } from './BookingCaseContext';

/**
 * Buscador de micros. Dos cosas lo distinguen del resto:
 *
 * 1. Cada oferta es un servicio EN UNA CALIDAD (Semi-cama, Cama...): el mismo
 *    micro aparece una vez por calidad, con las hermanas a un click.
 * 2. El IDA Y VUELTA es de DOS PASOS, porque el proveedor cotiza la vuelta
 *    ENCADENADA a la ida elegida (y puede repreciar la ida): primero se elige
 *    la ida, después se buscan las vueltas con su provenance, y el hold va con
 *    el token de la vuelta — que encadena ambos tramos en UN booking.
 *
 * El precio de búsqueda es la tarifa pelada: los impuestos de venta aparecen
 * en el hold (`priceIsIndicative`). Se dice en cada card, no al final.
 */
function payloadFromParams(
  params: Record<string, string>,
): BusesSearchPayload | null {
  if (params.buscar !== '1' || !params.origen || !params.destino || !params.fecha) {
    return null;
  }
  return {
    fromStation: params.origen,
    toStation: params.destino,
    date: params.fecha,
    ...(params.fechaVuelta ? { returnDate: params.fechaVuelta } : {}),
    passengers: numParam(params, 'pax', 1, { min: 1, max: 9 }),
  };
}

function stationFromParams(
  params: Record<string, string>,
  key: 'origen' | 'destino',
): CatalogEntry | null {
  const id = params[key];
  if (!id) return null;
  return { id, name: params[`${key}Nombre`] ?? id };
}

export function BusesSearchForm() {
  const { t, i18n } = useTranslation();
  const { isAgent } = useAudience();
  /**
   * Logos que no cargaron. Se lleva por URL y no por fila porque el estado
   * pertenece a la URL con la que falló: el hub rota los links del proveedor y
   * uno nuevo merece su propio intento, no heredar el fracaso del anterior.
   */
  const [failedLogos, setFailedLogos] = useState<string[]>([]);
  const url = useSearchUrlState({ vertical: 'buses', dateParam: 'fecha' });
  const searchPayload = useMemo(
    () => payloadFromParams(url.params),
    [url.params],
  );
  const search = useSearchBuses(searchPayload);

  const [fromStation, setFromStation] = useState<CatalogEntry | null>(() =>
    stationFromParams(url.params, 'origen'));
  const [toStation, setToStation] = useState<CatalogEntry | null>(() =>
    stationFromParams(url.params, 'destino'));
  const [date, setDate] = useState(() => url.params.fecha ?? '');
  const [withReturn, setWithReturn] = useState(() => Boolean(url.params.fechaVuelta));
  const [returnDate, setReturnDate] = useState(() => url.params.fechaVuelta ?? '');
  const [returnDateOpen, setReturnDateOpen] = useState(false);
  const [passengers, setPassengers] = useState(() =>
    numParam(url.params, 'pax', 1, { min: 1, max: 9 }));
  const { partySuggestion, consumePartySuggestion } = useBookingCaseContext();
  useEffect(() => {
    if (!partySuggestion) return;
    setPassengers(partySuggestion.size);
    consumePartySuggestion(partySuggestion.revision);
  }, [partySuggestion, consumePartySuggestion]);
  const [filters, setFilters] = useState<BusFilters>(() => ({
    ...EMPTY_BUS_FILTERS,
    classes: listParam(url.params, 'fClase'),
    operators: listParam(url.params, 'fOperador'),
    directOnly: url.params.fDirecto === '1',
  }));
  const [sort, setSort] = useState<BusSort>(() => enumParam(
    url.params,
    'orden',
    ['departure', 'price', 'duration'] as const,
    'departure',
  ));
  const currentMainSearchIdentity = mainBusSearchIdentity(CONFIG.AGENCY_ID, searchPayload);
  const [quoteChain, setQuoteChain] = useState(emptyBusQuoteChain);
  const { bookingQuote } = quoteChain;
  const bookingOffer = bookingQuote?.offer ?? null;

  /**
   * Paso 2 del ida y vuelta: la ida ELEGIDA. Vive en estado y no en la URL a
   * propósito — el provenance mide ~5KB y vence a los 15 min: serializarlo
   * sería compartir links muertos. Refrescar la página vuelve al paso 1, y
   * está bien: las vueltas cuelgan de una sesión del proveedor que ya no está.
   */
  const outboundQuote =
    quoteChain.outboundQuote?.searchIdentity === currentMainSearchIdentity ? quoteChain.outboundQuote : null;
  const outbound = outboundQuote?.offer ?? null;
  const returnPayload = useMemo<BusesSearchPayload | null>(() => busReturnPayload(outboundQuote), [outboundQuote]);
  const returnSearch = useSearchBuses(returnPayload);

  useEffect(() => {
    setQuoteChain((current) => reconcileBusChainWithMainSearch(current, currentMainSearchIdentity));
  }, [currentMainSearchIdentity]);

  // Los refinamientos viajan en la URL (sin tocar el historial).
  useEffect(() => {
    url.update({
      fClase: serializeList(filters.classes),
      fOperador: serializeList(filters.operators),
      fDirecto: filters.directOnly ? '1' : undefined,
      orden: sort === 'departure' ? undefined : sort,
    });
  }, [filters, sort, url]);

  const isRoundTrip = Boolean(searchPayload?.returnDate);

  /**
   * Paradas del PEDIDO para el mapa del recorrido (#2255). La referencia
   * comiteada es el CÓDIGO de la parada, así que se RESUELVE contra el
   * catálogo —pedido con el nombre comiteado, que es por lo que el hub filtra—
   * y se queda con la entrada de ESE código: una búsqueda reconstruida desde
   * la URL o desde recientes tiene mapa igual. Viaja la PÁGINA entera porque
   * resolver exige saber si se vieron todos los candidatos; la entrada viva
   * del formulario es solo un atajo (ver `busRouteMapPoints`).
   *
   * Hoy el catálogo del proveedor no publica coordenadas, así que no hay
   * puntos y no hay card. El día que las publique (rutero#2230) el mapa
   * aparece solo, sin tocar nada de acá.
   */
  const fromStationCatalog = useBusStationsResolution(
    searchPayload ? url.params.origenNombre : undefined,
  );
  const toStationCatalog = useBusStationsResolution(
    searchPayload ? url.params.destinoNombre : undefined,
  );
  const bookingRoutePoints = busRouteMapPoints(
    searchPayload,
    {
      selection: fromStation,
      catalog: fromStationCatalog.data,
      label: url.params.origenNombre,
    },
    {
      selection: toStation,
      catalog: toStationCatalog.data,
      label: url.params.destinoNombre,
    },
  );

  const minDate = todaySearchDate();
  const minReturnDate = latestSearchDate(minDate, date);
  const canSearch = Boolean(
    fromStation &&
      toStation &&
      date &&
      date >= minDate &&
      (!withReturn || returnDate >= minReturnDate),
  );

  /**
   * El formulario tal como se escribiría en la URL. Es la ÚNICA derivación del
   * borrador: de acá salen la búsqueda que se confirma y el payload contra el
   * que se compara la comprometida (21.22), no dos expresiones parecidas.
   */
  const formParams: UrlParams = {
    buscar: '1',
    origen: fromStation?.id,
    origenNombre: fromStation?.name,
    destino: toStation?.id,
    destinoNombre: toStation?.name,
    fecha: date,
    fechaVuelta: withReturn && returnDate ? returnDate : undefined,
    pax: String(passengers),
  };
  const draftPayload = payloadFromParams(cleanParams(formParams));
  /**
   * La divergencia se mide contra la búsqueda PRINCIPAL, también en el paso 2:
   * las vueltas cuelgan de la ida elegida, que a su vez cuelga de esa búsqueda
   * — si el formulario ya no la describe, la cadena entera es de otra consulta.
   */
  const formDiverged = searchFormDiverged(searchPayload, draftPayload);

  /** Vacía la búsqueda: campos, filtros, URL y memoria de navegación. */
  const handleClear = () => {
    setFromStation(null);
    setToStation(null);
    setDate('');
    setWithReturn(false);
    setReturnDate('');
    setPassengers(1);
    setFilters(EMPTY_BUS_FILTERS);
    setQuoteChain(emptyBusQuoteChain());
    url.reset();
  };

  const handleSearch = () => {
    if (!canSearch || !fromStation || !toStation) return;
    // Los filtros son facetas del resultado anterior; la ida elegida también.
    setFilters(EMPTY_BUS_FILTERS);
    setQuoteChain(emptyBusQuoteChain());
    if (
      shouldRefetchExpiredIdenticalSearch(
        currentMainSearchIdentity,
        mainBusSearchIdentity(CONFIG.AGENCY_ID, draftPayload),
        Boolean(search.data),
        search.dataUpdatedAt,
        Date.now(),
      )
    ) {
      void search.refetch();
    }
    url.commit(formParams);
  };

  const swap = () => {
    setFromStation(toStation);
    setToStation(fromStation);
  };

  /** Qué lista está en pantalla: idas (paso 1) o vueltas (paso 2). */
  const activeSearch = outbound ? returnSearch : search;
  const resultAuthority = useSearchResultAuthority(activeSearch, formDiverged);
  /** Vigencia de la generación que se está mostrando (§ barra de resumen). */
  const searchCountdown = useFareCountdown(
    activeSearch.data ? activeSearch.dataUpdatedAt : undefined,
  );
  const returnProvenanceExpired = Boolean(outbound && returnSearch.isError && isProvenanceExpired(returnSearch.error));
  const activeSearchIdentity = returnProvenanceExpired
    ? undefined
    : outbound
      ? returnPayload
        ? quoteSearchIdentity('buses', CONFIG.AGENCY_ID, returnPayload)
        : undefined
      : currentMainSearchIdentity;
  const allOffers = useMemo(() => (activeSearch.data?.results ?? []) as BusOffer[], [activeSearch.data]);
  const offers = useMemo(() => sortBuses(filterBuses(allOffers, filters), sort), [allOffers, filters, sort]);
  const classes = useMemo(() => [...new Set(allOffers.map(seatClassName).filter(Boolean))].sort(), [allOffers]);
  const operators = useMemo(
    () => [...new Set(allOffers.map((o) => String(o.operator ?? '')).filter(Boolean))].sort(),
    [allOffers],
  );
  const facet = (patch: Partial<BusFilters>) =>
    busFacetCount(allOffers, filters, patch);
  const activeFilterCount =
    filters.classes.length + filters.operators.length + (filters.directOnly ? 1 : 0);
  const toggleInList = <T,>(list: T[], value: T): T[] =>
    list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

  /**
   * El token de la ida venció mientras el asesor elegía la vuelta: volver al
   * paso 1 con la búsqueda intacta, no mostrar un proveedor "caído".
   */
  useEffect(() => {
    if (returnProvenanceExpired) {
      setQuoteChain(emptyBusQuoteChain(true, currentMainSearchIdentity));
    }
  }, [returnProvenanceExpired, currentMainSearchIdentity]);

  const pickOffer = (offer: BusOffer) => {
    if (formDiverged || returnProvenanceExpired) return;
    if (!isQuoteFreshForHold(activeSearch.dataUpdatedAt, Date.now())) return;
    if (isRoundTrip && !outbound) {
      // Paso 1 → 2: elegir la ida dispara la búsqueda de vueltas. Los filtros
      // eran de la lista de idas: no aplican a la de vueltas.
      const selectedOutbound = captureOutboundBusQuote(offer, CONFIG.AGENCY_ID, {
        payload: searchPayload,
        quotedAt: search.dataUpdatedAt,
      });
      if (!selectedOutbound) return;
      setQuoteChain(busChainWithOutbound(selectedOutbound));
      setFilters(EMPTY_BUS_FILTERS);
      return;
    }
    const selectedFinal = captureFinalBusQuote(
      offer,
      CONFIG.AGENCY_ID,
      { payload: searchPayload, quotedAt: search.dataUpdatedAt },
      outbound
        ? {
          payload: returnPayload,
          quotedAt: returnSearch.dataUpdatedAt,
        }
        : undefined,
    );
    if (!selectedFinal) return;
    setQuoteChain((current) => busChainWithFinalQuote(current, selectedFinal));
  };

  const offerCard = (offer: BusOffer, index: number) => {
    const direct = isDirect(offer);
    const nextDay = arrivesNextDay(offer);
    const publishedLogoUrl = offer.operatorLogoUrl as string | undefined;
    const operatorLogoUrl =
      publishedLogoUrl && !failedLogos.includes(publishedLogoUrl)
        ? publishedLogoUrl
        : undefined;
    const operatorName = String(offer.operator ?? '');
    const seatClass = seatClassName(offer);
    /** Hechos del servicio: texto quieto con separadores, no una sopa de chips. */
    const facts = [
      seatClass,
      offer.onboardAttendant ? t('travelServices.buses.attendant') : '',
    ].filter(Boolean);
    // En la vuelta manda el TOTAL ida+vuelta del proveedor (con su repricing
    // de la ida si lo hubo); el tramo queda de detalle.
    const roundTripTotal = offer.leg === 'return' ? offer.roundTripTotal : undefined;

    return (
      <RuteroResultCard
        key={offer.provenance.slice(-24)}
        index={index}
        header={
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {/* El logo va acá, a su tamaño real: en la caja de media se
                recortaba con `cover` y le comía el nombre a la empresa. */}
            {operatorLogoUrl ? (
              <Box
                component="img"
                src={operatorLogoUrl}
                alt={operatorName}
                loading="lazy"
                sx={{ height: 18, maxWidth: 72, objectFit: 'contain' }}
                onError={() =>
                  setFailedLogos((current) =>
                    current.includes(operatorLogoUrl)
                      ? current
                      : [...current, operatorLogoUrl],)}
              />
            ) : (
              <DirectionsBus sx={{ fontSize: 18 }} color="disabled" />
            )}
            {operatorName && (
              <Typography component="span" sx={{ fontSize: 13, fontWeight: 600 }}>
                {operatorName}
              </Typography>
            )}
            {/* Por dónde se compra es información de operación: en una
                superficie minorista no se muestra. */}
            {isAgent && operatorName && offer.provider !== operatorName && (
              <Typography component="span" color="text.disabled" sx={{ fontSize: 11.5 }}>
                {t('travelServices.viaProvider', { provider: offer.provider })}
              </Typography>
            )}
            {/* Solo con el dato real del proveedor: sin `freeSeats` no se
                inventa una escasez que no sabemos si existe. */}
            {hasFewSeats(offer) && (
              <Box ml="auto">
                <StatusFlag
                  tone="warn"
                  label={t('travelServices.buses.fewSeats', { count: offer.freeSeats })}
                />
              </Box>
            )}
          </Stack>
        }
        title={
          <ScheduleTimes
            departure={{
              time: busTime(offer.departure),
              place: String(offer.fromStation ?? '—'),
            }}
            arrival={{
              time: busTime(offer.arrival),
              place: String(offer.toStation ?? '—'),
              mark: nextDay ? busDay(offer.arrival) : undefined,
            }}
            duration={formatMinutes(offer.durationMinutes) ?? ''}
            note={
              direct
                ? t('travelServices.buses.direct')
                : t('travelServices.buses.connection')
            }
          />
        }
        details={<MetaLine items={facts} />}
        price={
          <Box textAlign="right">
            {roundTripTotal ? (
              <>
                <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatMoney(roundTripTotal)}
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  {t('travelServices.buses.roundTripTotal')}
                </Typography>
              </>
            ) : (
              <PriceBlock pricing={offer.pricing} />
            )}
            {/* El precio de búsqueda es la tarifa pelada del asiento: los
                impuestos de venta aparecen recién en el hold. */}
            {offer.priceIsIndicative && (
              <Typography variant="caption" color="warning.dark" display="block">
                {t('travelServices.buses.plusTaxes')}
              </Typography>
            )}
          </Box>
        }
        action={
          <Button
            variant="contained"
            sx={{ minWidth: 104, height: 30 }}
            disabled={!resultAuthority.canSelectResult || returnProvenanceExpired}
            onClick={() => pickOffer(offer)}
          >
            {isRoundTrip && !outbound
              ? t('travelServices.buses.pickOutbound')
              : t('travelServices.book')}
          </Button>
        }
      >
        {(offer.otherSeatClasses?.length ?? 0) > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('travelServices.buses.otherClasses', {
              classes: (offer.otherSeatClasses ?? [])
                .map((sc) =>
                  sc.price
                    ? `${sc.name ?? sc.code} ${formatMoney(sc.price)}`
                    : sc.name ?? sc.code,)
                .join(' · '),
            })}
          </Typography>
        )}
      </RuteroResultCard>
    );
  };

  /**
   * El ida y vuelta es UNA reserva encadenada: el resumen del modal muestra los
   * DOS tramos, porque el hold cubre a los dos. La ida entra solo si sigue
   * siendo la de la búsqueda vigente (`outbound`).
   */
  const bookingDetails = bookingOffer
    ? busBookingDetailLines(bookingOffer, outbound, searchPayload?.passengers ?? 1, {
      outbound: t('travelServices.booking.outbound'),
      return: t('travelServices.booking.return'),
      pax: t('travelServices.pax'),
    })
    : undefined;

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <SearchBar
        onClear={url.hasParams ? handleClear : undefined}
        onSearch={handleSearch}
        loading={search.isLoading}
        disabled={!canSearch}
      >
        <SearchField label={t('travelServices.buses.from')} grow width={220}>
          <BusStationInput
            label={t('travelServices.buses.from')}
            value={fromStation}
            onChange={setFromStation}
          />
        </SearchField>
        <Tooltip title={t('travelServices.transfers.swap')}>
          <IconButton size="small" onClick={swap} sx={{ alignSelf: 'center' }}>
            <SwapHoriz />
          </IconButton>
        </Tooltip>
        <SearchField label={t('travelServices.buses.to')} grow width={220}>
          <BusStationInput
            label={t('travelServices.buses.to')}
            value={toStation}
            onChange={setToStation}
          />
        </SearchField>
        <SearchDivider />
        <SearchField
          label={t('travelServices.transfers.date')}
          width={SEARCH_DATE_FIELD_WIDTH}
          xsShare={SEARCH_FIELD_HALF_SHARE}
        >
          <SearchDatePicker
            label={t('travelServices.transfers.date')}
            value={date}
            minDate={minDate}
            onChange={(nextDate) => {
              setDate(nextDate);
              setReturnDate((current) => reconcileSearchEndDate(nextDate, current));
            }}
            onAccept={() => {
              if (withReturn) setReturnDateOpen(true);
            }}
            required
          />
        </SearchField>
        {withReturn && (
          <>
            <SearchDivider />
            <SearchField
              label={t('travelServices.buses.returnDate')}
              width={SEARCH_DATE_FIELD_WIDTH}
              xsShare={SEARCH_FIELD_HALF_SHARE}
            >
              <SearchDatePicker
                label={t('travelServices.buses.returnDate')}
                value={returnDate}
                onChange={setReturnDate}
                minDate={minReturnDate}
                open={returnDateOpen}
                onOpen={() => setReturnDateOpen(true)}
                onClose={() => setReturnDateOpen(false)}
                required
              />
            </SearchField>
          </>
        )}
        <SearchDivider />
        <SearchField
          label={t('travelServices.passengerPicker.label')}
          width={SEARCH_PASSENGER_FIELD_WIDTH}
        >
          <SearchPassengerPicker
            value={countOnlyPassengers(passengers)}
            onChange={(nextPassengers) => setPassengers(nextPassengers.length)}
            rules={COUNT_ONLY_PASSENGER_RULES}
          />
        </SearchField>
      </SearchBar>

      <FormControlLabel
        control={
          <Switch
            size="small"
            checked={withReturn}
            onChange={(event) => {
              setWithReturn(event.target.checked);
              if (!event.target.checked) setReturnDateOpen(false);
            }}
          />
        }
        label={
          <Typography variant="body2">
            {t('travelServices.buses.withReturn')}
          </Typography>
        }
      />

      {url.params.buscar !== '1' && (
        <RecentSearches
          vertical="buses"
          describe={(params) => describeBuses(params, t, i18n.language)}
        />
      )}

      {isIncompleteSearch(url.params, searchPayload) && (
        <Alert severity="warning">{t('travelServices.incompleteLink')}</Alert>
      )}

      {/* ── Paso a paso del ida y vuelta ────────────────────────────────── */}
      {isRoundTrip && search.data && (
        <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
          <Chip
            size="small"
            color={outbound ? 'default' : 'primary'}
            label={t('travelServices.buses.stepOutbound')}
          />
          <Chip
            size="small"
            color={outbound ? 'primary' : 'default'}
            variant={outbound ? 'filled' : 'outlined'}
            label={t('travelServices.buses.stepReturn')}
          />
          {outbound && (
            <>
              <Typography variant="body2" color="text.secondary">
                {t('travelServices.buses.outboundChosen', {
                  time: `${busDay(outbound.departure)} ${busTime(outbound.departure)}`,
                  seatClass: seatClassName(outbound),
                })}
              </Typography>
              <Button size="small" onClick={() => setQuoteChain(emptyBusQuoteChain())}>
                {t('travelServices.buses.changeOutbound')}
              </Button>
            </>
          )}
        </Box>
      )}

      {quoteChain.outboundExpired && <Alert severity="warning">{t('travelServices.buses.outboundExpired')}</Alert>}

      {activeSearch.isLoading && <ResultsSkeleton archetype="schedule" />}
      {activeSearch.isError && !returnProvenanceExpired && (
        <Alert severity="error">{t('travelServices.searchError')}</Alert>
      )}
      <SearchResultGenerationStatus authority={resultAuthority} />
      {url.params.buscar === '1' && searchPayload && (
        <ActiveSearchSummary
          label={t('travelServices.activeSearchSummary')}
          lines={[describeBuses(url.params, t, i18n.language)]}
          countdown={searchCountdown}
          onRefresh={() => void activeSearch.refetch()}
        />
      )}

      {/* El recorrido de la búsqueda comprometida, uno por búsqueda y
          colapsado: el iframe no existe hasta que el asesor lo abre. La
          identidad de la búsqueda va de `key` para que una búsqueda nueva
          arranque cerrada y sin el mapa de la anterior. En el ida y vuelta
          los dos tramos unen las mismas dos paradas: el mapa es el mismo en
          los dos pasos. */}
      {url.params.buscar === '1' && searchPayload && (
        <SearchRouteMap
          key={searchRouteMapKey(activeSearchIdentity, bookingRoutePoints)}
          vertical="buses"
          points={bookingRoutePoints}
        />
      )}
      {!search.data && !search.isLoading && !search.isError && (
        <Typography variant="body2" color="text.secondary">
          {t('travelServices.buses.emptyState')}
        </Typography>
      )}

      {activeSearch.data && !activeSearch.isLoading && (
        <>
          <ProviderStatusStrip providers={activeSearch.data.providers} />

          {allOffers.length > 0 && (
            <ResultFiltersBar
              activeCount={activeFilterCount}
              onClear={() => setFilters(EMPTY_BUS_FILTERS)}
              resultCount={offers.length}
              sort={{
                value: sort,
                onChange: (value) => setSort(value as BusSort),
                options: [
                  { value: 'departure', label: t('travelServices.buses.sortDeparture') },
                  { value: 'price', label: t('travelServices.cars.sortPrice') },
                  { value: 'duration', label: t('travelServices.buses.sortDuration') },
                ],
              }}
            >
              {(stacked) => (
                <>
                  {classes.length > 1 && (
                    <FilterGroup
                      label={t('travelServices.buses.classFilter')}
                      stacked={stacked}
                    >
                      {classes.map((seatClass) => (
                        <FilterChip
                          key={seatClass}
                          label={seatClass}
                          count={facet({ classes: [seatClass] })}
                          active={filters.classes.includes(seatClass)}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              classes: toggleInList(prev.classes, seatClass),
                            }))
                          }
                        />
                      ))}
                    </FilterGroup>
                  )}
                  {operators.length > 1 && (
                    <FilterGroup
                      label={t('travelServices.buses.operatorFilter')}
                      stacked={stacked}
                    >
                      {operators.map((operator) => (
                        <FilterChip
                          key={operator}
                          label={operator}
                          count={facet({ operators: [operator] })}
                          active={filters.operators.includes(operator)}
                          onClick={() =>
                            setFilters((prev) => ({
                              ...prev,
                              operators: toggleInList(prev.operators, operator),
                            }))
                          }
                        />
                      ))}
                    </FilterGroup>
                  )}
                  <FilterGroup
                    label={t('travelServices.buses.serviceFilter')}
                    stacked={stacked}
                  >
                    <FilterChip
                      label={t('travelServices.buses.direct')}
                      count={facet({ directOnly: true })}
                      active={filters.directOnly}
                      onClick={() =>
                        setFilters((prev) => ({
                          ...prev,
                          directOnly: !prev.directOnly,
                        }))
                      }
                    />
                  </FilterGroup>
                </>
              )}
            </ResultFiltersBar>
          )}

          <Typography variant="body2" color="text.secondary">
            {outbound
              ? t('travelServices.buses.returnsFound', { count: offers.length })
              : t('travelServices.buses.found', { count: offers.length })}
          </Typography>

          {offers.length === 0 && allOffers.length === 0 && (
            <Alert severity="info">{t('travelServices.buses.noResults')}</Alert>
          )}

          <Box display="flex" flexDirection="column" gap={1.5}>
            {offers.map(offerCard)}
          </Box>
        </>
      )}

      <BookingModal
        vertical="buses"
        quote={bookingQuote}
        activeSearchIdentity={activeSearchIdentity}
        details={bookingDetails}
        routePoints={bookingRoutePoints}
        party={committedParty({ vertical: 'buses', payload: searchPayload })}
        onClose={() => setQuoteChain((current) => ({ ...current, bookingQuote: null }))}
      />
    </Box>
  );
}
